/**
 * PortfolioBreakdown Component
 *
 * Tabbed interface showing AI portfolio positions with allocation bars.
 * Includes "Copy Portfolio" button to create a new portfolio from AI allocations.
 * Non-subscribers see locked state with PRO badge.
 */

import { FC, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { colors, spacing, fontStyles } from '../../styles';
import {
  getInvestingArenaPositions,
  getInvestingArenaPortfolios,
  InvestingArenaPosition,
  AIModelId,
  AI_MODEL_NAMES,
  AI_MODEL_COLORS,
  AI_MODEL_ICONS,
} from '../../apis/investingArena';
import { savePortfolio, Allocation } from '../../storage';
import * as stores from '../../stores';
import Button from '../Button';
import StockLogo from '../StockLogo';

const AI_MODEL_ORDER: AIModelId[] = ['claude', 'openai', 'gemini'];

const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

// Skeleton loader for the breakdown
const PortfolioBreakdownSkeleton: FC = () => {
  return (
    <div
      css={css`
        padding: ${spacing.deci};
        border-radius: 12px;
        background: var(--card-background);
      `}
    >
      {/* Tabs skeleton */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
          margin-bottom: ${spacing.deci};
        `}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            css={css`
              width: 80px;
              height: 32px;
              border-radius: 16px;
              background: ${colors.cloud};
              ${skeletonPulse}
              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        ))}
      </div>
      {/* Table skeleton */}
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.centi};
            padding: ${spacing.centi} 0;
            border-bottom: 1px solid var(--border-color);
          `}
        >
          <div
            css={css`
              width: 32px;
              height: 32px;
              border-radius: 8px;
              background: ${colors.cloud};
              ${skeletonPulse}
              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
          <div
            css={css`
              flex: 1;
            `}
          >
            <div
              css={css`
                width: 50px;
                height: 14px;
                border-radius: 4px;
                background: ${colors.cloud};
                margin-bottom: ${spacing.micro};
                ${skeletonPulse}
                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
            <div
              css={css`
                width: 80px;
                height: 8px;
                border-radius: 4px;
                background: ${colors.cloud};
                ${skeletonPulse}
                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
          </div>
          <div
            css={css`
              width: 50px;
              height: 14px;
              border-radius: 4px;
              background: ${colors.cloud};
              ${skeletonPulse}
              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        </div>
      ))}
    </div>
  );
};

// Locked state for non-subscribers
const LockedBreakdown: FC = () => {
  const { t } = useTranslation();
  const handleUnlock = () => {
    stores.showPaymentModalByPreference('ai_portfolio_breakdown');
  };

  return (
    <div
      css={css`
        padding: ${spacing.deci};
        border-radius: 8px;
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};
        text-align: center;

        html[data-theme='dark'] & {
          background: var(--background-color);
          border-color: ${colors.tungsten};
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.lion}
          margin: 0 0 ${spacing.centi} 0;
          color: var(--text-color);
        `}
      >
        {t('aiarena.portfolioBreakdown')}
      </h3>

      <p
        css={css`
          ${fontStyles.cat}
          color: ${colors.silver};
          margin: 0 0 ${spacing.deci} 0;
        `}
      >
        {t('aiarena.lockedDescription')}
      </p>

      <Button variant="primary" onClick={handleUnlock}>
        {t('aiarena.unlockWithPro')}
      </Button>
    </div>
  );
};

interface PositionRowProps {
  position: InvestingArenaPosition;
  allocation: number;
  maxAllocation: number;
  onClick: () => void;
}

const PositionRow: FC<PositionRowProps> = ({ position, allocation, maxAllocation, onClick }) => {
  // API returns unrealized_plpc (percentage) - use it directly
  // Fallback to calculation if not provided
  const pnlPct =
    position.unrealized_plpc ??
    position.unrealized_pnl_pct ??
    (position.avg_cost > 0 ? ((position.current_price - position.avg_cost) / position.avg_cost) * 100 : 0);
  const isPositive = pnlPct >= 0;
  const returnColor = isPositive ? colors.leaf : colors.berry;
  const allocationWidth = (allocation / maxAllocation) * 100;

  return (
    <button
      onClick={onClick}
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing.centi};
        padding: ${spacing.centi} 0;
        width: 100%;
        border: none;
        background: none;
        cursor: pointer;
        text-align: left;
        border-bottom: 1px solid var(--border-color);

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: var(--card-background);
        }
      `}
    >
      <StockLogo symbol={position.symbol} size="small" />

      <div
        css={css`
          flex: 1;
          min-width: 0;
        `}
      >
        <div
          css={css`
            ${fontStyles.fatCat}
            color: var(--text-color);
          `}
        >
          {position.symbol}
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.milli};
            margin-top: ${spacing.micro};
          `}
        >
          <div
            css={css`
              flex: 1;
              height: 6px;
              background: var(--border-color);
              border-radius: 3px;
              overflow: hidden;
            `}
          >
            <div
              css={css`
                width: ${allocationWidth}%;
                height: 100%;
                background: ${colors.petrolBlue};
                border-radius: 3px;
                transition: width 0.3s ease;

                html[data-theme='dark'] & {
                  background: ${colors.skyBlue};
                }
              `}
            />
          </div>
          <span
            css={css`
              ${fontStyles.flea}
              color: ${colors.silver};
              min-width: 40px;
              text-align: right;
            `}
          >
            {allocation.toFixed(1)}%
          </span>
        </div>
      </div>

      <div
        css={css`
          ${fontStyles.fatCat}
          color: ${returnColor};
          min-width: 60px;
          text-align: right;
        `}
      >
        {isPositive ? '+' : ''}
        {pnlPct.toFixed(2)}%
      </div>

      <span
        css={css`
          color: ${colors.silver};
          font-size: 16px;
        `}
      >
        &rsaquo;
      </span>
    </button>
  );
};

interface PortfolioBreakdownProps {
  initialModelId?: AIModelId;
}

export const PortfolioBreakdown: FC<PortfolioBreakdownProps> = ({ initialModelId = 'claude' }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedModel, setSelectedModel] = useState<AIModelId>(initialModelId);
  const [isCopying, setIsCopying] = useState(false);

  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const hasActiveSubscription = !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Fetch portfolios for account values (isError guard prevents crash when API throws)
  const { data: portfolios, isError: portfoliosError } = useQuery(
    ['investing-arena', 'portfolios'],
    getInvestingArenaPortfolios,
    {
      staleTime: 5 * 60 * 1000,
      retry: 0, // function throws on error; don't retry to avoid multi-second delay on failure
    },
  );

  // Fetch positions for selected model
  const { data: positions, isLoading } = useQuery(
    ['investing-arena', 'positions', selectedModel],
    () => getInvestingArenaPositions(selectedModel),
    {
      staleTime: 5 * 60 * 1000,
      enabled: hasActiveSubscription,
    },
  );

  // Get current portfolio's account value
  const currentPortfolio = useMemo(() => {
    return portfolios?.find((p) => p.id === selectedModel);
  }, [portfolios, selectedModel]);

  // Calculate allocations for positions
  const positionsWithAllocations = useMemo(() => {
    if (!positions || !currentPortfolio || currentPortfolio.account_value === 0) return [];

    return positions.map((pos) => ({
      ...pos,
      allocation: (pos.market_value / currentPortfolio.account_value) * 100,
    }));
  }, [positions, currentPortfolio]);

  // Find max allocation for bar scaling
  const maxAllocation = useMemo(() => {
    if (positionsWithAllocations.length === 0) return 100;
    return Math.max(...positionsWithAllocations.map((p) => p.allocation));
  }, [positionsWithAllocations]);

  // Handle copy portfolio
  const handleCopyPortfolio = async () => {
    if (!positions || !currentPortfolio || currentPortfolio.account_value === 0 || isCopying) return;

    setIsCopying(true);

    try {
      const allocations: Allocation[] = positions.map((pos) => ({
        symbol: pos.symbol,
        name: pos.symbol,
        allocation_percentage: (pos.market_value / currentPortfolio.account_value) * 100,
        rationale: `Copied from ${AI_MODEL_NAMES[selectedModel]} AI portfolio`,
      }));

      const portfolioId = await savePortfolio({
        title: `${AI_MODEL_NAMES[selectedModel]} Copy`,
        strategy: `AI-managed portfolio strategy from ${AI_MODEL_NAMES[selectedModel]}`,
        symbols: positions.map((p) => p.symbol),
        allocations,
      });

      // Auto-select the new portfolio
      localStorage.setItem('selectedPortfolioId', portfolioId);

      toast.success(t('aiarena.portfolioCopied'));
      history.push('/portfolios');
    } catch (error) {
      console.error('[PortfolioBreakdown] Failed to copy portfolio:', error);
      toast.error(t('aiarena.portfolioCopyFailed'));
    } finally {
      setIsCopying(false);
    }
  };

  // Handle position click
  const handlePositionClick = (symbol: string) => {
    history.push(`/symbol/${symbol}`);
  };

  // Show locked state for non-subscribers
  if (!hasActiveSubscription) {
    return <LockedBreakdown />;
  }

  // If portfolio data failed to load, render nothing rather than crash
  if (portfoliosError) {
    return null;
  }

  if (isLoading) {
    return <PortfolioBreakdownSkeleton />;
  }

  return (
    <div
      css={css`
        padding: ${spacing.deci};
        border-radius: 8px;
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};

        html[data-theme='dark'] & {
          background: var(--background-color);
          border-color: ${colors.tungsten};
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.lion}
          margin: 0 0 ${spacing.deci} 0;
          color: var(--text-color);
        `}
      >
        {t('aiarena.portfolioBreakdown')}
      </h3>

      {/* Model tabs */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.centi};
          margin-bottom: ${spacing.deci};
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        `}
      >
        {AI_MODEL_ORDER.map((modelId) => {
          const isSelected = modelId === selectedModel;
          const modelIcon = AI_MODEL_ICONS[modelId];
          const modelColor = AI_MODEL_COLORS[modelId];

          return (
            <button
              key={modelId}
              onClick={() => setSelectedModel(modelId)}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.milli};
                padding: ${spacing.milli} ${spacing.centi};
                border-radius: 16px;
                border: 1px solid ${isSelected ? modelColor : 'var(--border-color)'};
                background: ${isSelected ? `${modelColor}15` : 'transparent'};
                cursor: pointer;
                transition: all 0.15s ease;
                white-space: nowrap;

                &:hover {
                  border-color: ${modelColor};
                }
              `}
            >
              <div
                css={css`
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  background: white;
                  border: 1px solid ${colors.cloud};
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  html[data-theme='dark'] & {
                    background: #2a2a2a;
                    border-color: ${colors.tungsten};
                  }
                `}
              >
                {modelIcon && (
                  <img
                    src={modelIcon}
                    alt=""
                    css={css`
                      width: 10px;
                      height: 10px;

                      html[data-theme='dark'] & {
                        filter: ${modelId === 'openai' ? 'invert(1)' : 'none'};
                      }
                    `}
                  />
                )}
              </div>
              <span
                css={css`
                  ${fontStyles.flea}
                  color: ${isSelected ? modelColor : 'var(--text-color)'};
                  font-weight: ${isSelected ? 600 : 400};

                  html[data-theme='dark'] & {
                    color: ${isSelected && modelId === 'openai'
                      ? colors.snow
                      : isSelected
                        ? modelColor
                        : 'var(--text-color)'};
                  }
                `}
              >
                {AI_MODEL_NAMES[modelId]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Positions list */}
      <div
        css={css`
          margin-bottom: ${spacing.deci};
        `}
      >
        {positionsWithAllocations.length === 0 ? (
          <div
            css={css`
              ${fontStyles.cat}
              color: ${colors.silver};
              text-align: center;
              padding: ${spacing.deci};
            `}
          >
            {t('aiarena.noPositions')}
          </div>
        ) : (
          positionsWithAllocations.map((pos) => (
            <PositionRow
              key={pos.symbol}
              position={pos}
              allocation={pos.allocation}
              maxAllocation={maxAllocation}
              onClick={() => handlePositionClick(pos.symbol)}
            />
          ))
        )}
      </div>

      {/* Copy Portfolio button */}
      {positionsWithAllocations.length > 0 && (
        <Button variant="primary" block onClick={handleCopyPortfolio} disabled={isCopying}>
          {isCopying ? t('aiarena.copying') : t('aiarena.copyPortfolio', { model: AI_MODEL_NAMES[selectedModel] })}
        </Button>
      )}
    </div>
  );
};

export default PortfolioBreakdown;
